import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { usePrevious } from 'react-use'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { OrganizationsAPI, UsersAPI } from '@makyoapp/makyo-sdk'
import { fromSdkOrg, OrgCardAdapter } from '@makyoapp/makyo-common'
import { Loading } from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useCacheContext } from '@/context/CacheContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useParamId } from '@/hooks/useParamId'
import { useReqError } from '@/hooks/useReqError'
import { EVENT_KEYS } from '@/shared/custom-event'
import { SetterCallback } from '@/shared/types'
import { PageAccessDenied } from '@/components/PageAccessDenied'
import { PageNotFound } from '@/components/PageNotFound'

interface UpdateCurrentOrgParams {
  successMessage?: string
  onAfterUpdate?: (newOrg: OrgCardAdapter) => void
  onBeforeUpdate?: () => void
}

interface CurrentOrgState {
  currentOrg?: OrgCardAdapter
  setCurrentOrg: Dispatch<SetStateAction<OrgCardAdapter | undefined>>
  updateCurrentOrg: (params: UpdateCurrentOrgParams) => void
  updateAvatar: (
    newAvatar?: { src?: string; file?: File },
    options?: {
      beforeUpdate?: () => void
      successMessage?: string
      afterUpdate?: () => void
    }
  ) => void
  isLoading: boolean
}

const CurrentOrgContext = createContext<CurrentOrgState>({
  currentOrg: undefined,
  setCurrentOrg: () => null,
  updateCurrentOrg: () => null,
  updateAvatar: () => null,
  isLoading: false,
})

/**
 * use app context hook
 */
export function useCurrentOrgContext() {
  return useContext(CurrentOrgContext)
}

interface CurrentOrgProviderProps {
  children?: React.ReactNode
}

function updateGlobalOrgAvatar(
  currentOrg: OrgCardAdapter,
  globalOrgs: OrgCardAdapter[],
  newAvatar?: string,
  currentAvatar?: string
) {
  const newGlobal = [...globalOrgs]
  const idx = newGlobal.findIndex((o) => o.id === currentOrg.id)

  if (idx !== -1) {
    if (currentAvatar?.startsWith('blob:')) {
      URL.revokeObjectURL(currentAvatar)
    }
    newGlobal[idx].avatar = newAvatar
  }

  return newGlobal
}

/**
 * AppContext provider
 */
export const CurrentOrgProvider = ({ children }: CurrentOrgProviderProps) => {
  const { imageCache } = useCacheContext()
  const printAPIError = useReqError()

  const queryClient = useQueryClient()

  const paramOrgId = useParamId('org')
  const isProjectPage = useParamId('project') !== undefined

  const { orgs, setOrgs, orgsPerms, setOrgsPerms } = useOrgsContext()
  const { project } = useSingleProjectContext()
  const orgId = isProjectPage && project ? project.org.id : paramOrgId

  const prevOrgId = usePrevious(orgId)

  const initialCurrentOrg = useMemo(() => orgs.find((o) => o.id === orgId), [orgs, orgId])
  const [isAuthorized, setIsAuthorized] = useState(!!initialCurrentOrg)

  // ============================ Current Org ============================

  const currentOrgQuery = useQuery({
    queryKey: [queryKeys.org, orgId],
    queryFn: async () => {
      if (!orgId) return null

      try {
        const orgRes = await OrganizationsAPI.ReqGet(orgId)
        if (!orgRes) {
          setIsAuthorized(false)
          return null
        }

        // update org permission
        UsersAPI.ReqGetOrgPermissions(orgId).then((orgPerms) => {
          setOrgsPerms((prev) => ({ ...prev, [orgId]: orgPerms }))
        })
        setIsAuthorized(true)
        return fromSdkOrg(orgRes)
      } catch (error) {
        setIsAuthorized(false)
        return null
      }
    },
    enabled: !!orgId,
  })

  const currentOrg = useMemo(() => currentOrgQuery.data ?? undefined, [currentOrgQuery.data])
  const currentOrgPerms = useMemo(() => orgsPerms[orgId || ''], [orgsPerms, orgId])
  const isLoading = currentOrgQuery.isLoading || !currentOrg || !currentOrgPerms

  const setCurrentOrg = useCallback(
    (cb: SetterCallback<OrgCardAdapter | undefined>) => {
      queryClient.setQueryData([queryKeys.org, orgId], cb)
    },
    [orgId, queryClient]
  )

  const updateCurrentOrg = useCallback(
    ({ successMessage, onAfterUpdate, onBeforeUpdate }: UpdateCurrentOrgParams) => {
      if (orgId) {
        onBeforeUpdate?.()

        OrganizationsAPI.ReqGet(orgId)
          .then((org) => {
            const newOrg = fromSdkOrg(org)
            setCurrentOrg(fromSdkOrg(org))
            onAfterUpdate?.(newOrg)
            if (successMessage) {
              toasted(successMessage)
            }
          })
          .catch((e) => {})
      }
    },
    [orgId, setCurrentOrg]
  )

  const updateAvatar = useCallback(
    (
      avatar?: {
        src?: string
        file?: File
      },
      options?: {
        beforeUpdate?: () => void
        successMessage?: string
        afterUpdate?: () => void
      }
    ) => {
      if (currentOrg) {
        const currentAvatar = currentOrg?.avatar
        if (avatar?.file && avatar?.src) {
          OrganizationsAPI.ReqUpdateAvatar(currentOrg.id, avatar.file)
            .then(() => {
              setCurrentOrg({
                ...currentOrg,
                avatar: avatar.src,
              })
              setOrgs(updateGlobalOrgAvatar(currentOrg, orgs, avatar.src, currentAvatar))

              // update image cache
              imageCache.update(`org-${currentOrg.id}`, Date.now())

              if (options?.successMessage) {
                toasted(options.successMessage)
              }
            })
            .catch(printAPIError)
            .finally(() => {
              options?.afterUpdate?.()
            })
        } else {
          OrganizationsAPI.ReqDeleteAvatar(currentOrg.id)
            .then(() => {
              setCurrentOrg({
                ...currentOrg,
                avatar: undefined,
              })
              setOrgs(updateGlobalOrgAvatar(currentOrg, orgs, undefined, currentAvatar))

              if (options?.successMessage) {
                toasted(options.successMessage)
              }
            })
            .catch(printAPIError)
            .finally(() => {
              options?.afterUpdate?.()
            })
        }
      }
    },
    [currentOrg, printAPIError, setCurrentOrg, setOrgs, orgs, imageCache]
  )

  // ============================ Effects ============================

  /**
   * update org accessed time
   */
  useEffect(() => {
    if (prevOrgId !== orgId) {
      const idx = orgs.findIndex((o) => o.id === orgId)
      if (idx !== -1) {
        setOrgs((prev) => {
          const copy = [...prev]
          copy[idx].accessed = new Date()
          return copy
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, prevOrgId, setOrgs])

  // fire global listener is unauthorized
  useEffect(() => {
    const event = new CustomEvent(EVENT_KEYS.ORG_AUTHORIZATION_CHANGE, {
      detail: { isAuthorized: Boolean(isAuthorized) },
    })
    window.dispatchEvent(event)
  }, [isAuthorized])

  // ============================ Render ============================
  if (currentOrgQuery.isFetched && !currentOrg && !isAuthorized) {
    return <PageAccessDenied />
  }

  if (isLoading) {
    return <Loading fullHeight />
  }

  if (!currentOrg) {
    return <PageNotFound />
  }

  const providerValues = {
    currentOrg,
    setCurrentOrg,
    updateCurrentOrg,
    updateAvatar,
    isLoading,
  }

  return <CurrentOrgContext.Provider value={providerValues}>{children}</CurrentOrgContext.Provider>
}
